import { useMemo } from "react";
import { matchPath, useLocation, useNavigate, useParams } from "react-router-dom";
import { Container, Icon, Tooltip } from "@/components/atoms";
import { Tabs } from "@/components/molecules";
import { PageHeader } from "@/components/organisms";
import { AffiliateVehiclesActions } from "@/components/organisms/Vehicles/AffiliateVehicles/AffiliateVehiclesActions";
import { AffiliateVehiclesActionsMenu } from "@/components/organisms/Vehicles/AffiliateVehicles/AffiliateVehiclesActionsMenu";
import { ArchivedVehiclesActionMenu } from "@/components/organisms/Vehicles/ArchivedVehicles/ArchivedVehiclesActionMenu";
import { ArchivedVehiclesActions } from "@/components/organisms/Vehicles/ArchivedVehicles/ArchivedVehiclesActions";
import { MyVehiclesActionMenu } from "@/components/organisms/Vehicles/MyVehicles/MyVehiclesActionMenu";
import { MyVehiclesActions } from "@/components/organisms/Vehicles/MyVehicles/MyVehiclesActions";
import { VehicleFiltersProvider } from "@/components/organisms/Vehicles/features/Filters/useVehicleFilters";
import { useOperator, useSubscriptionAssignment } from "@/hooks";
import { getPlanLimits, shouldShowLimitIndicator } from "@/redux/apis/subscription/subscriptionLimits";
import { useGetVehicleQuery, useGetVehiclesQuery } from "@/redux/apis/vehicle/vehicleApi";
import { RequireAccess } from "@/routes/RequireAccess/RequireAccess";
import { AffiliateVehicles } from "./AffiliateVehicles";
import { ArchivedVehicles } from "./ArchivedVehicles";
import { MyVehicles } from "./MyVehicles";

export const Vehicles = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id: operatorId } = useOperator();
  const { subscriptionStatus } = useSubscriptionAssignment();

  // Get current vehicle count for My Vehicles tab
  const { data: vehiclesData } = useGetVehiclesQuery(
    {
      owner_uuid: operatorId ?? "",
      page_size: "1",
      page: "1",
    },
    {
      skip: !operatorId,
    }
  );

  const planSlug = subscriptionStatus.baseSubscription?.subscription.name.toLowerCase() ?? "";
  const limits = getPlanLimits(planSlug);
  const showLimitIndicator = shouldShowLimitIndicator(planSlug);
  const vehicleCount = vehiclesData?.page.total ?? 0;

  const isAtVehicleLimit = vehicleCount >= limits.maxVehicles;
  const remainingVehicles = limits.maxVehicles - vehicleCount;

  const handleUpgradeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/operator/${operatorId}/payments/subscriptions/subscription-plan`);
  };

  const vehicleTooltipContent = isAtVehicleLimit
    ? `You've reached the maximum of ${limits.maxVehicles} vehicles for your current plan. Archive an existing vehicle or `
    : `This plan allows up to ${limits.maxVehicles} vehicles. You can add ${remainingVehicles} more.`;

  const vehicleTooltipIcon = showLimitIndicator ? (
    <Tooltip
      content={
        isAtVehicleLimit ? (
          <span>
            You&apos;ve reached the maximum of {limits.maxVehicles} vehicles for your current plan. Archive an existing vehicle or{" "}
            <a href="#" className="text-info underline" onClick={handleUpgradeClick}>
              upgrade
            </a>{" "}
            your plan to add more.
          </span>
        ) : (
          vehicleTooltipContent
        )
      }
      placement="top-end"
      interactive={isAtVehicleLimit}
      interactiveBorder={20}
      interactiveDebounce={0}
      appendTo={() => document.body}
    >
      <Icon name="InfoCircle" size="md" className="ml-2 rotate-180 text-neutral-silver-gray" variant="Bold" />
    </Tooltip>
  ) : undefined;

  const pageTabs = [
    {
      name: showLimitIndicator ? `My Vehicles (${vehicleCount}/${limits.maxVehicles})` : "My Vehicles",
      to: "my-vehicles",
      icon: vehicleTooltipIcon,
    },
    {
      name: "My Drivers' Vehicles",
      to: "affiliate-vehicles",
    },
    {
      name: "Archived Vehicles",
      to: "archived-vehicles",
    },
    {
      name: "Vehicle Category",
      to: `/operator/${operatorId}/config/trip/vehicle-categories`,
      icon: <Icon name="ExportSquare" size="md" className="ml-2" />,
    },
  ];

  const isParentPage = Boolean(
    matchPath({ path: "/vehicles/:slug" }, pathname) || matchPath({ path: "operator/:operatorId/vehicles/:slug" }, pathname)
  );

  return (
    <VehicleFiltersProvider>
      <Container className="w-full">
        <div className="sticky top-0 z-30 w-full bg-white ">
          <CustomHeader />
          {isParentPage && (
            <div className="flex w-full gap-2 border-b !border-neutral-mid-gray py-1 max-xl:justify-between lg:py-0">
              <Tabs className="no-scrollbar min-w-0 border-none" tabs={pageTabs} />
              <ActionMenu />
            </div>
          )}
          <Actions />
        </div>
        <RequireAccess action="view" subject="providerConfigVehicle" />
      </Container>
    </VehicleFiltersProvider>
  );
};

const ActionMenu = () => {
  const { pathname } = useLocation();

  switch (true) {
    case pathname.includes("/vehicles/my-vehicles"):
      return <MyVehiclesActionMenu />;
    case pathname.includes("/vehicles/archived-vehicles"):
      return <ArchivedVehiclesActionMenu />;
    case pathname.includes("/vehicles/affiliate-vehicles"):
      return <AffiliateVehiclesActionsMenu />;
    default:
      return null;
  }
};

const Actions = () => {
  const { pathname } = useLocation();

  switch (true) {
    case pathname.includes("/vehicles/my-vehicles"):
      return <MyVehiclesActions />;
    case pathname.includes("/vehicles/archived-vehicles"):
      return <ArchivedVehiclesActions />;
    case pathname.includes("/vehicles/affiliate-vehicles"):
      return <AffiliateVehiclesActions />;
    default:
      return null;
  }
};

const CustomHeader = () => {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const { data } = useGetVehicleQuery(vehicleId || "", {
    skip: !vehicleId,
  });

  const moreCrumbs = useMemo(() => {
    if (!vehicleId || !data) return undefined;

    return [
      {
        name: `${data.model.make} ${data.model.model} (${data.registrationNumber})`,
        to: `#`,
        current: true,
      },
    ];
  }, [data, vehicleId]);

  return <PageHeader title="Vehicles" skipCrumbs={1} moreCrumbs={moreCrumbs} />;
};

Vehicles.MyVehicles = MyVehicles;
Vehicles.AffiliateVehicles = AffiliateVehicles;
Vehicles.ArchivedVehicles = ArchivedVehicles;
Vehicles.AffiliateVehicles = AffiliateVehicles;
